// Code Generator for OrbitCustomizer SaaS
// Generates React component code from config

export const generateReactComponent = (config) => {
  const {
    radius,
    menuOffset,
    buttonSize,
    logoText,
    logoImage,
    logoType,
    menuItemFontSize,
    buttonBgColor,
    buttonOutlineColor,
    buttonOutlineWidth,
    menuItemBgColor,
    menuItemOutlineColor,
    menuItemOutlineWidth,
    menuItemTextColor,
    logoStiffness,
    logoDamping,
  } = config;

  const logoImageCode = logoType === 'image' && logoImage
    ? `
            {logoImage && (
              <img
                src={logoImage}
                alt="Logo"
                style={{
                  width: '70%',
                  height: '70%',
                  objectFit: 'contain'
                }}
              />
            )}`
    : '';

  const logoTextCode = logoType === 'text' ? `{logoText || '${logoText}'}` : '';

  return `import React, { useState } from 'react';
import { motion } from 'framer-motion';

const OrbitMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoRotation, setLogoRotation] = useState(0);

  // Configuration
  const config = {
    radius: ${radius},
    menuOffset: ${menuOffset},
    buttonSize: ${buttonSize},
    logoText: '${logoText}',
    logoType: '${logoType}',
    menuItemFontSize: ${menuItemFontSize},
    colors: {
      buttonBg: '${buttonBgColor}',
      buttonOutline: '${buttonOutlineColor}',
      buttonOutlineWidth: ${buttonOutlineWidth},
      menuItemBg: '${menuItemBgColor}',
      menuItemText: '${menuItemTextColor}',
      menuItemOutline: '${menuItemOutlineColor}',
      menuItemOutlineWidth: ${menuItemOutlineWidth},
    },
    animation: {
      logoStiffness: ${logoStiffness},
      logoDamping: ${logoDamping},
    }
  };

  const menuItems = [
    { id: 1, angle: 0, label: 'Menu 1', route: '/page1' },
    { id: 2, angle: -45, label: 'Menu 2', route: '/page2' },
    { id: 3, angle: -90, label: 'Menu 3', route: '/page3' },
    { id: 4, angle: -135, label: 'Menu 4', route: '/page4' },
    { id: 5, angle: -180, label: 'Home', route: '/' },
  ];

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setLogoRotation(isOpen ? 0 : 180);
  };

  return (
    <div style={{ position: 'fixed', bottom: '2em', right: '2em', zIndex: 1000 }}>
      {/* Center Button */}
      <motion.div
        onClick={handleToggle}
        animate={{ rotate: logoRotation }}
        transition={{
          type: 'spring',
          stiffness: config.animation.logoStiffness,
          damping: config.animation.logoDamping
        }}
        style={{
          position: 'relative',
          width: config.buttonSize,
          height: config.buttonSize,
          backgroundColor: config.colors.buttonBg,
          border: config.colors.buttonOutlineWidth > 0
            ? \`\${config.colors.buttonOutlineWidth}px solid \${config.colors.buttonOutline}\`
            : 'none',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#fff',
          cursor: 'pointer',
          overflow: 'hidden',
          transform: \`translateY(\${config.menuOffset}px)\`,
        }}
      >${logoImageCode}${logoTextCode}
      </motion.div>

      {/* Menu Items */}
      {menuItems.map((item, index) => {
        const angleRad = (item.angle * Math.PI) / 180;
        const targetRadius = isOpen ? config.radius : 0;
        const x = Math.cos(angleRad) * targetRadius;
        const y = Math.sin(angleRad) * targetRadius;

        return (
          <motion.div
            key={item.id}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              x,
              y,
              scale: isOpen ? 1 : 0,
              opacity: isOpen ? 1 : 0
            }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20,
              delay: index * 0.05,
            }}
            style={{
              position: 'absolute',
              left: config.buttonSize / 2,
              top: config.buttonSize / 2 + config.menuOffset,
              transform: 'translate(-50%, -50%)',
              width: config.buttonSize,
              height: config.buttonSize,
              backgroundColor: config.colors.menuItemBg,
              border: \`\${config.colors.menuItemOutlineWidth}px solid \${config.colors.menuItemOutline}\`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: config.menuItemFontSize,
              color: config.colors.menuItemText,
              cursor: 'pointer',
              fontFamily: 'monospace',
              textAlign: 'center',
              padding: '4px',
              pointerEvents: isOpen ? 'auto' : 'none',
            }}
            onClick={() => {
              // Handle navigation here
              console.log('Navigate to:', item.route);
            }}
          >
            {item.label}
          </motion.div>
        );
      })}
    </div>
  );
};

export default OrbitMenu;
`;
};

export const generateInstallationGuide = (config) => {
  return `# OrbitMenu Installation Guide

## Step 1: Install Dependencies

\`\`\`bash
npm install framer-motion
# or
yarn add framer-motion
\`\`\`

## Step 2: Copy the Component

Copy the \`OrbitMenu.jsx\` component to your project:
\`\`\`
src/components/OrbitMenu.jsx
\`\`\`

## Step 3: Use in Your App

\`\`\`jsx
import OrbitMenu from './components/OrbitMenu';

function App() {
  return (
    <div>
      {/* Your app content */}
      <OrbitMenu />
    </div>
  );
}
\`\`\`

## Step 4: Customize (Optional)

Edit the \`menuItems\` array in \`OrbitMenu.jsx\` to add your own menu items:

\`\`\`jsx
const menuItems = [
  { id: 1, angle: 0, label: 'Your Label', route: '/your-route' },
  // Add more items...
];
\`\`\`

## Configuration

Your current configuration:
- **Radius:** ${config.radius}px
- **Menu Offset:** ${config.menuOffset}px
- **Button Size:** ${config.buttonSize}px
- **Logo Type:** ${config.logoType}
${config.logoType === 'text' ? `- **Logo Text:** "${config.logoText}"` : '- **Logo:** Custom Image'}

## Need Help?

- 📧 Email: support@orbitcustomizer.com
- 💬 Discord: discord.gg/orbitcustomizer
- 📖 Docs: docs.orbitcustomizer.com

---

🚀 Generated with **OrbitCustomizer** - https://orbitcustomizer.com
`;
};

export const generateVanillaJS = (config) => {
  return `<!-- Vanilla JS version coming soon! -->
<!-- For now, use the React version or contact us for a custom implementation -->
<script>
  console.log('OrbitMenu Config:', ${JSON.stringify(config, null, 2)});
</script>
`;
};

export const generateCSS = (config) => {
  const {
    buttonSize,
    buttonBgColor,
    buttonOutlineColor,
    buttonOutlineWidth,
    menuItemBgColor,
    menuItemTextColor,
    menuItemOutlineColor,
    menuItemOutlineWidth,
    menuItemFontSize,
  } = config;

  return `/* OrbitMenu Styles */

.orbit-menu {
  position: fixed;
  bottom: 2em;
  right: 2em;
  z-index: 1000;
}

.orbit-menu__button {
  width: ${buttonSize}px;
  height: ${buttonSize}px;
  background-color: ${buttonBgColor};
  border: ${buttonOutlineWidth}px solid ${buttonOutlineColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.orbit-menu__button:hover {
  transform: scale(1.05);
}

.orbit-menu__button--open {
  transform: rotate(180deg);
}

.orbit-menu__item {
  position: absolute;
  width: ${buttonSize}px;
  height: ${buttonSize}px;
  background-color: ${menuItemBgColor};
  border: ${menuItemOutlineWidth}px solid ${menuItemOutlineColor};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${menuItemFontSize}px;
  color: ${menuItemTextColor};
  cursor: pointer;
  font-family: monospace;
  text-align: center;
  padding: 4px;
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease;
}

.orbit-menu__item:hover {
  transform: scale(1.1);
  z-index: 10;
}
`;
};
